/* Generate uuid */
/**
 * @return {string}
 */
export function CreateUUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export function formatApiDateToLong(
  dateStr: string,
  addOneDay = false,
): string {
  const date = new Date(dateStr);
  if (addOneDay) {
    date.setDate(date.getDate() + 1);
  }
  const options: Intl.DateTimeFormatOptions = {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  };
  return date.toLocaleDateString('en-GB', options);
}

export function formatToYen(amount: string | undefined): string {
  if (amount === undefined) return '¥ -';
  const numericValue = parseFloat(amount);
  return new Intl.NumberFormat('ja-JP', {
    style: 'currency',
    currency: 'JPY',
    maximumFractionDigits: 0,
  }).format(numericValue);
}

export const CreateUUIDWithoutDash = () => CreateUUID().replace(/-/g, '');

/* Authentication helpers for sagas */
import { select } from 'redux-saga/effects';
import { loginSelectors } from '../containers/Auth/Login/selectors';
import SessionDB from './sessionStorage';

/**
 * Debug helper to check all token storage locations
 */
export function debugTokenStorage() {


  // Check localStorage
  const localStorageToken = localStorage.getItem('authToken');
  const localStorageUser = localStorage.getItem('user');
  
  

  // Check sessionStorage
  const sessionToken = SessionDB.getAccessToken();
  

  // Check if there are any other auth-related keys
  
  


}

/**
 * Saga helper to get authentication token from Redux state with fallback to storage
 * @returns {Generator} - Token string or null
 */
export function* getAuthToken(): Generator<any, string | null, any> {
  try {
    // Debug storage state
    debugTokenStorage();

    // First try to get token from Redux state (login reducer)
    const loginReduxToken: string | null = yield select(loginSelectors.token);
    

    if (loginReduxToken) {
      return loginReduxToken;
    }

    // Then try app reducer
    const { appSelectors } = yield import('../containers/App/selectors');
    const appReduxToken: string | null = yield select(appSelectors.token);
    

    if (appReduxToken) {
      return appReduxToken;
    }

    // Fallback to localStorage (where login saga stores it)
    const localStorageToken = localStorage.getItem('authToken');
    if (localStorageToken) {
      return localStorageToken;
    }

    // Secondary fallback to session storage
    const sessionToken = SessionDB.getAccessToken();
    if (sessionToken) {
      return sessionToken;
    }

    
    return null;
  } catch (error) {
    
    return null;
  }
}

/**
 * Manual debug helper - call this from browser console
 * Usage: window.debugAuth()
 */
export function debugAuthState() {


  // Storage debug
  debugTokenStorage();

  // Redux state debug (if store is available)
  try {
    const store = (window as any).__REDUX_STORE__ || (window as any).store;
    if (store) {
      const state = store.getState();
      
      
      
      
      
    } else {
      
    }
  } catch (error) {
    
  }


}

/**
 * Test helper to manually trigger auth check (for debugging)
 * Usage: window.testCheckAuth()
 */
export function testCheckAuth() {
  

  // Check what's in localStorage first
  debugTokenStorage();

  try {
    const store = (window as any).__REDUX_STORE__ || (window as any).store;
    if (store) {
      // Import the checkAuth action
      import('../containers/Auth/Login/actions').then(({ checkAuth }) => {
        
        store.dispatch(checkAuth());

        // Check state after a moment
        setTimeout(() => {
          const state = store.getState();
          
          
          
          
        }, 1000);
      });
    }
  } catch (error) {
    
  }
}

// Make debug and test functions available globally
if (typeof window !== 'undefined') {
  (window as any).debugAuth = debugAuthState;
  (window as any).testCheckAuth = testCheckAuth;
  (window as any).testTokenRefresh = testTokenRefresh;
}

/**
 * Test token refresh functionality (for debugging)
 * Usage: window.testTokenRefresh()
 */
export function testTokenRefresh() {
  

  try {
    const store = (window as any).__REDUX_STORE__ || (window as any).store;
    if (store) {
      // Import the refresh token action
      import('../containers/Auth/Login/actions').then(
        ({ refreshTokenRequest }) => {
          
          store.dispatch(refreshTokenRequest());

          // Check state after a moment
          setTimeout(() => {
            const state = store.getState();
            
            
          
            
          }, 2000);
        },
      );
    }
  } catch (error) {
   
  }
}

/**
 * Refresh access token using refresh token from cookie
 * @returns {Promise<string|null>} - New access token or null if refresh failed
 */
export async function refreshAccessToken(): Promise<string | null> {
  try {
    const refreshToken = SessionDB.getRefreshTokenFromCookie();

    if (!refreshToken) {
      
      return null;
    }

    

    // Import API constants and schema dynamically to avoid circular dependencies
    const [{ API_ENDPOINTS }, { validateRefreshTokenResponse }] =
      await Promise.all([
        import('../globalConstants'),
        import('../apischema/login/refreshToken'),
      ]);

    const response = await fetch(API_ENDPOINTS.auth.REFRESH_TOKEN, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Cookie: `refreshToken=${refreshToken}`,
      },
      credentials: 'include', // Include cookies
    });

    if (!response.ok) {
      
      return null;
    }

    const data = await response.json();

    try {
      // Validate the response using Zod schema
      const validatedResponse = validateRefreshTokenResponse(data);

      

      // Store new access token
      localStorage.setItem('authToken', validatedResponse.accessToken);
      SessionDB.setAccessToken(validatedResponse.accessToken);

      return validatedResponse.accessToken;
    } catch (validationError) {
      
      return null;
    }
  } catch (error) {
    
    return null;
  }
}
